import { memo, useCallback, useRef, useState } from 'react';
import { Badge, Button, Card, Input, TextArea } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Pencil, Trash2 } from 'lucide-react';

import { ROLE_KIND_META } from '../../constants/copy';
import { ROLE_TYPES } from '../../constants/roles';
import { PERMISSIONS } from '../../constants/permissions';
import Can from '../guards/Can';

const FIELD = Object.freeze({ NAME: 'name', DESCRIPTION: 'description' });

/**
 * Header card of the role detail, as in the prototype: name with its type badge and description
 * on the left, the member and permission counts on the right. A custom role (manage permission)
 * edits its name and description in place (pencil, then Save / Cancel) and can be deleted from the
 * trash button. A system role is read only.
 *
 * @param {object} props
 * @param {object} props.role UI role (see `mapApiRoleToUi`)
 * @param {(patch: { name?: string, description?: string }) => Promise<{ ok: boolean, status?: number }>} props.onSaveDetails
 *   `ok` ends the edit; a 409 shows "name already in use" inline.
 * @param {() => void} props.onDelete
 */
const RoleDetailHeader = ({ role, onSaveDetails, onDelete }) => {
  const { t } = useTranslation('people-access');
  const kind = ROLE_KIND_META[role.isSystem ? ROLE_TYPES.SYSTEM : ROLE_TYPES.CUSTOM];
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState('');
  const [taken, setTaken] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // The ref blocks a second click before React re-renders with `isSaving`.
  const savingRef = useRef(false);

  const startName = useCallback(() => {
    setValue(role.name);
    setTaken(false);
    setEditing(FIELD.NAME);
  }, [role.name]);
  const startDescription = useCallback(() => {
    setValue(role.description);
    setTaken(false);
    setEditing(FIELD.DESCRIPTION);
  }, [role.description]);
  const cancel = useCallback(() => {
    setEditing(null);
    setTaken(false);
  }, []);
  const handleChange = useCallback((event) => {
    setValue(event.target.value);
    setTaken(false);
  }, []);

  const canSave = editing === FIELD.DESCRIPTION || value.trim().length > 0;
  const save = useCallback(async () => {
    if (savingRef.current || !canSave) return;
    savingRef.current = true;
    setIsSaving(true);
    let result = { ok: false };
    try {
      result = await onSaveDetails({ [editing]: value.trim() });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
    if (result.ok) setEditing(null);
    else if (result.status === 409) setTaken(true);
  }, [canSave, editing, onSaveDetails, value]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && editing === FIELD.NAME) {
        event.preventDefault();
        save();
      } else if (event.key === 'Escape') {
        event.stopPropagation();
        cancel();
      }
    },
    [editing, save, cancel],
  );

  const editActions = (
    <div className="mt-2 flex gap-2">
      <Button size="sm" variant="secondary" onClick={save} disabled={!canSave} loading={isSaving}>
        {t('roles.detail.edit.save')}
      </Button>
      <Button size="sm" variant="ghost" onClick={cancel} disabled={isSaving}>
        {t('roles.detail.edit.cancel')}
      </Button>
    </div>
  );

  const pencil = (field, label, onClick) => (
    <Can permission={PERMISSIONS.ROLES_MANAGE}>
      {role.isSystem || editing === field ? null : (
        <Button variant="ghost" size="sm" leftIcon={Pencil} aria-label={label} onClick={onClick} />
      )}
    </Can>
  );

  return (
    <Card data-testid="role-header">
      <div className="flex flex-wrap items-start justify-between gap-4 p-6">
        <div className="min-w-0 flex-1">
          {editing === FIELD.NAME ? (
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  aria-label={t('roles.detail.edit.nameLabel')}
                  value={value}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  error={taken ? t('roles.detail.edit.nameTaken', { name: value.trim() }) : undefined}
                  disabled={isSaving}
                  className="min-w-[14rem]"
                  autoFocus
                />
                <Badge size="small" variant={kind.variant}>
                  {t(kind.labelKey)}
                </Badge>
              </div>
              {editActions}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-lg font-semibold text-content">{role.name}</h1>
              <Badge size="small" variant={kind.variant}>
                {t(kind.labelKey)}
              </Badge>
              {pencil(FIELD.NAME, t('roles.detail.edit.nameAria'), startName)}
            </div>
          )}
          {editing === FIELD.DESCRIPTION ? (
            <div className="mt-2">
              <TextArea
                aria-label={t('roles.detail.edit.descriptionLabel')}
                rows={2}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={isSaving}
                autoFocus
              />
              {editActions}
            </div>
          ) : (
            <div className="mt-1 flex items-center gap-1.5">
              <p className="text-sm text-content-secondary">{role.description}</p>
              {pencil(FIELD.DESCRIPTION, t('roles.detail.edit.descriptionAria'), startDescription)}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-6">
          <div className="text-center" data-testid="role-stat-members">
            <p className="text-xl font-semibold leading-tight text-content">{role.memberCount}</p>
            <p className="text-xs text-content-muted">{t('roles.detail.stats.members')}</p>
          </div>
          <div className="text-center" data-testid="role-stat-permissions">
            <p className="text-xl font-semibold leading-tight text-content">{role.permissionCount}</p>
            <p className="text-xs text-content-muted">{t('roles.detail.stats.permissions')}</p>
          </div>
          {role.isSystem ? null : (
            <Can permission={PERMISSIONS.ROLES_MANAGE}>
              <Button variant="ghost" size="sm" leftIcon={Trash2} aria-label={t('roles.detail.deleteRole')} onClick={onDelete} />
            </Can>
          )}
        </div>
      </div>
    </Card>
  );
};

export default memo(RoleDetailHeader);
